import { useState } from 'react';

import { Badge } from '../../../../../../components/badge';
import { Button } from '../../../../../../components/buttons/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '../../../../../../components/dialog';
import {
  Currency,
  type OrganizationBilling,
  PlanKind,
  SubscriptionPaymentCycle,
} from '../../../../../../entity/api/organization-billing';
import { tNamespace } from '../../../../../../services/translation';
import { formatCurrency } from '../../../../../../shared/currency';

const t = tNamespace('components.trial-limit-plan-modal');
const tBilling = tNamespace('app.billing.general');

export type TrialLimitPlanModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationBilling?: {
    currentCurrency: OrganizationBilling['currentCurrency'];
    plan?: Pick<
      NonNullable<OrganizationBilling['plan']>,
      'kind' | 'paymentCycle' | 'pricePerMonth' | 'priceDiscount' | 'trialArticles'
    > | null;
  } | null;
  onConfirm?: () => Promise<unknown>;
  onSkip?: () => void;
  userLanguage?: string;
};

export function TrialLimitPlanModal(props: TrialLimitPlanModalProps) {
  const { open, onOpenChange, organizationBilling, onConfirm, onSkip, userLanguage } = props;

  const pricePerMonth = organizationBilling?.plan?.pricePerMonth ?? 0;
  const currency = organizationBilling?.currentCurrency ?? Currency.Usd;
  const planKind = organizationBilling?.plan?.kind ?? PlanKind.Custom;
  const billingCycle = organizationBilling?.plan?.paymentCycle ?? SubscriptionPaymentCycle.Month;
  const isYearly = billingCycle === SubscriptionPaymentCycle.Year;
  const billingCycleText = tBilling(
    billingCycle === SubscriptionPaymentCycle.Year ? 'yearly' : 'monthly'
  );
  const discountPercent = organizationBilling?.plan?.priceDiscount ?? 0;
  const trialArticles = organizationBilling?.plan?.trialArticles ?? 1;
  const [isLoading, setIsLoading] = useState(false);

  function handleConfirm() {
    if (onConfirm != null) {
      setIsLoading(true);

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      onConfirm().finally(function () {
        setIsLoading(false);
      });
    }
  }

  function handleSkip() {
    if (onSkip != null) {
      onSkip();
    } else {
      onOpenChange(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hasCloseButton className="w-full max-w-[800px]">
        <div className="flex flex-col gap-8">
          <div>
            {/* Title */}
            <DialogTitle align="center">
              <div className="mt-13 flex flex-col gap-6">
                <div className="mx-auto text-center">
                  <div className="relative">
                    <img
                      src="/sparkles.png"
                      alt="Sparkles"
                      width={45}
                      height={45}
                      className="absolute -left-[41px] top-[6px] -translate-y-full"
                    />
                    <img
                      src="/single-sparkle.png"
                      alt="Sparkles"
                      width={24}
                      height={24}
                      className="absolute -right-[18px] bottom-[9px] translate-y-full"
                    />
                    <p className="font-display-2 text-center">
                      {t('title', { count: trialArticles })}
                    </p>
                  </div>
                </div>
              </div>
            </DialogTitle>
            <div className="font-body-2-sb mb-4 mt-4 text-center text-branding-gray-600">
              {t('subtitle')}
            </div>
          </div>

          <div className="mx-auto flex w-full max-w-[500px] flex-col gap-4">
            {/* Divider */}
            <div className="h-px w-full bg-branding-gray-200" />

            {/* Plan Section */}
            <div className="flex flex-col gap-1">
              <p className="font-body-2-sb text-gray">{tBilling('plan')}</p>
              <div className="flex gap-1">
                <p className="font-body-2-sb uppercase text-branding-accent-800">{planKind}</p>
                <p className="font-body-2-sb uppercase text-branding-accent-600">
                  {billingCycleText}
                </p>
              </div>
            </div>

            {/* Divider */}
            <div className="h-px w-full bg-branding-gray-200" />

            {/* Total Section */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1">
                  <p className="font-body-2-sb">{tBilling('total')}</p>
                  <div className="flex items-end gap-1">
                    <p className="font-display-1 text-branding-accent-600">
                      {formatCurrency(pricePerMonth, currency, userLanguage)}
                      {tBilling('perMonthShort')}
                    </p>
                    {isYearly ? (
                      <p className="font-body-2-r mb-1 text-gray">{t('billedAnnually')}</p>
                    ) : null}
                  </div>
                </div>
                {discountPercent != null && discountPercent > 0 ? (
                  <Badge size="sm" color="accent-contrast">
                    {tBilling('percentOff', { percent: discountPercent })}
                  </Badge>
                ) : null}
              </div>
            </div>

            <div className="h-px w-full bg-branding-gray-200" />

            <div className="font-body-3-r text-gray">{t('chargeNotice')}</div>
          </div>

          {/* Action Buttons */}
          <DialogFooter isColumn className="mx-auto mt-4 max-w-[360px]">
            <div className="flex w-full gap-3">
              <Button
                size="xl"
                variant="accent"
                className="flex-1"
                onClick={handleConfirm}
                loading={isLoading}
              >
                {t('confirmBtn')}
              </Button>
            </div>
            <Button size="xl" variant="ghost-gray" className="w-full" onClick={handleSkip}>
              {t('skipBtn')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
